import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { S3UploadResponse, S3MultipartUploadResponse } from '../../entities';

/**
 * Service for S3-specific operations
 * Handles direct S3 uploads using presigned URLs
 */
@Injectable({
  providedIn: 'root'
})
export class S3Service {
  protected http = inject(HttpClient);

  private setHeaders(headers: { [key: string]: string }): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    if (headers === null) { return httpHeaders; }
    for (const key in headers) {
      if (headers.hasOwnProperty(key) && headers[key] !== null && headers[key] !== undefined && headers[key].trim() !== '') {
        httpHeaders = httpHeaders.set(key, headers[key]);
      }
    }
    return httpHeaders;
  }

  /**
   * Upload file to S3 using presigned URL
   *
   * @param url - Presigned S3 URL
   * @param file - File to upload
   * @param params - Option HTTP query parameters
   * @param headers - Optional HTTP headers
   * @returns Observable with upload response
   */
  public uploadFile(
    url: string,
    file: File,
    params?: { [key: string]: any },
    headers?: { [key: string]: string }
  ): Observable<S3UploadResponse> {
    const httpHeaders = this.setHeaders(headers || {});
    return this.http.put(url, file, {
      headers: httpHeaders,
      params: params,
      observe: 'response',
      reportProgress: true
    }).pipe(
      map(response => ({
        statusCode: response.status,
        success: response.status === 200
      }))
    );
  }

  /**
   * Upload blob to S3 using presigned URL for multipart uploads
   * Returns ETag required for completing multipart upload
   *
   * @param url - Presigned S3 URL for this part
   * @param blob - Blob data to upload
   * @param params - Option HTTP query parameters
   * @param headers - Optional HTTP headers
   * @returns Observable with upload response including ETag
   */
  public uploadMultipartFile(
    url: string,
    blob: Blob,
    params?: { [key: string]: any },
    headers?: { [key: string]: string }
  ): Observable<S3MultipartUploadResponse> {
    const httpHeaders = this.setHeaders(headers || {});
    return this.http.put(url, blob, {
      headers: httpHeaders,
      params: params,
      observe: 'response',
      reportProgress: true
    }).pipe(
      map(response => {
        const eTag = response.headers.get('ETag');
        if (!eTag) {
          throw new Error('No ETag in S3 response');
        }
        return {
          eTag: eTag.replace(/"/g, ''), // Remove quotes from ETag
          statusCode: response.status,
          success: response.status === 200
        };
      })
    );
  }
}
